import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { accountClosedCode, api, freshAccessToken } from '@/services/api'
import type { Notification, OrderActivityScope, UserPresence, UserSocketEvent } from '@/types/api'

/** Unread notifications on one order, split by the page that clears them. */
export interface OrderActivity {
  messages: number
  updates: number
  /** The newest unread notification on the order. */
  latest: Notification
}

/** `seen` is true when the page showing it is open, so it was marked read instead. */
type NotificationHandler = (notification: Notification, seen: boolean) => void

export function orderIdOf(notification: Notification): string | undefined {
  return notification.data?.orderId || undefined
}

export function scopeOf(notification: Notification): OrderActivityScope {
  return notification.type === 'new_message' ? 'messages' : 'updates'
}

/** Where to go to see an order's new activity. */
export function activityPath(orderId: string, scope: OrderActivityScope) {
  return scope === 'messages' ? `/chat/${orderId}/` : `/order/${orderId}/`
}

// The backend emits uppercase UUIDs; compare IDs case-insensitively.
const idKey = (id: string) => id.toLowerCase()
const createdTime = (n: Notification) => new Date(n.createdAt ?? 0).getTime()

function socketUrl(token: string) {
  const origin = import.meta.env.VITE_WS_URL
    || `${window.location.protocol === 'https:' ? 'wss:' : 'ws:'}//${window.location.host}`
  return `${origin}/api/v1/ws/me?token=${encodeURIComponent(token)}`
}

// A backgrounded app gives up its socket (and so its "online" status) after this.
const hiddenGraceMs = 30_000
const maxBackoffMs = 30_000
const stableAfterMs = 10_000

/**
 * The app-wide user socket (`/ws/me`): counterpart presence and live
 * notifications. Unread state is the server's unread list, patched by pushes
 * and resynced whenever the socket (re)connects.
 */
export const useRealtimeStore = defineStore('realtime', () => {
  const presence = ref<Record<string, UserPresence>>({})
  const unread = ref<Notification[]>([])

  // Pages showing an order's chat or updates. Plain array: only read in handlers.
  const focused: { orderId: string; scope: OrderActivityScope }[] = []
  const handlers = new Set<NotificationHandler>()

  let running = false
  let connecting = false
  let socket: WebSocket | null = null
  let attempts = 0
  let reconnectTimer: ReturnType<typeof setTimeout> | undefined
  let stableTimer: ReturnType<typeof setTimeout> | undefined
  let hiddenTimer: ReturnType<typeof setTimeout> | undefined

  const activityByOrder = computed(() => {
    const byOrder = new Map<string, OrderActivity>()
    for (const notification of unread.value) {
      const orderId = orderIdOf(notification)
      if (!orderId) continue
      const activity = byOrder.get(idKey(orderId)) ?? { messages: 0, updates: 0, latest: notification }
      activity[scopeOf(notification)]++
      if (createdTime(notification) > createdTime(activity.latest)) activity.latest = notification
      byOrder.set(idKey(orderId), activity)
    }
    return byOrder
  })

  /** Unread notifications about orders: new messages and order updates. */
  const unreadCount = computed(() => unread.value.filter(orderIdOf).length)

  function activityFor(orderId: string): OrderActivity | undefined {
    return activityByOrder.value.get(idKey(orderId))
  }

  function presenceOf(userId: string): UserPresence | undefined {
    return presence.value[idKey(userId)]
  }

  function matches(notification: Notification, orderId: string, scope: OrderActivityScope) {
    const notificationOrder = orderIdOf(notification)
    return !!notificationOrder && idKey(notificationOrder) === idKey(orderId) && scopeOf(notification) === scope
  }

  function isFocused(notification: Notification) {
    return focused.some(f => matches(notification, f.orderId, f.scope))
  }

  async function markRead(orderId: string, scope: OrderActivityScope) {
    unread.value = unread.value.filter(n => !matches(n, orderId, scope))
    try {
      await api.markOrderNotificationsRead(orderId, scope)
    } catch (err) {
      console.error('Failed to mark notifications read:', err)
    }
  }

  /**
   * Call while a page shows an order's chat (`messages`) or its status
   * (`updates`): marks that activity read now and as it arrives. Returns the
   * release function for when the page goes away.
   */
  function focusOrder(orderId: string, scope: OrderActivityScope): () => void {
    const entry = { orderId, scope }
    focused.push(entry)
    void markRead(orderId, scope)
    return () => {
      const index = focused.indexOf(entry)
      if (index >= 0) focused.splice(index, 1)
    }
  }

  function onNotification(handler: NotificationHandler): () => void {
    handlers.add(handler)
    return () => handlers.delete(handler)
  }

  async function refreshUnread() {
    try {
      const page = await api.getNotifications(1, 50, true)
      if (!running) return // signed out meanwhile
      unread.value = page.data
      for (const { orderId, scope } of focused) {
        if (unread.value.some(n => matches(n, orderId, scope))) void markRead(orderId, scope)
      }
    } catch (err) {
      console.error('Failed to load unread notifications:', err)
    }
  }

  function receive(notification: Notification) {
    const seen = isFocused(notification)
    const orderId = orderIdOf(notification)
    if (seen && orderId) {
      void markRead(orderId, scopeOf(notification))
    } else if (!unread.value.some(n => n.id === notification.id)) {
      unread.value = [notification, ...unread.value]
    }
    handlers.forEach(handler => handler(notification, seen))
  }

  function handleEvent(event: UserSocketEvent) {
    if (event.type === 'presence') {
      const next = { ...presence.value }
      for (const user of event.presence) next[idKey(user.userID)] = user
      presence.value = next
    } else if (event.type === 'notification') {
      receive(event.notification)
    }
  }

  async function connect() {
    clearTimeout(reconnectTimer)
    if (!running || socket || connecting || document.hidden) return
    connecting = true
    let token: string | null
    try {
      await refreshUnread()
      // The server closes the socket when its token expires; start with one
      // that has some life left.
      token = await freshAccessToken()
    } finally {
      connecting = false
    }
    if (!running || socket || document.hidden || !token) return

    const ws = new WebSocket(socketUrl(token))
    socket = ws
    ws.addEventListener('open', () => {
      // Reset the backoff only once the socket stays up, so a server that
      // accepts and immediately closes does not get hammered.
      stableTimer = setTimeout(() => { attempts = 0 }, stableAfterMs)
    })
    ws.addEventListener('message', (message) => {
      try {
        handleEvent(JSON.parse(message.data) as UserSocketEvent)
      } catch (err) {
        console.error('Failed to parse user socket event:', err)
      }
    })
    ws.addEventListener('close', (event) => {
      if (socket !== ws) return // replaced or closed on purpose
      socket = null
      clearTimeout(stableTimer)
      // 4003: the account was banned or deleted; reconnecting cannot succeed.
      if (event.code === accountClosedCode) return
      scheduleReconnect()
    })
  }

  function scheduleReconnect() {
    if (!running || document.hidden) return
    attempts++
    // Exponential backoff: 1s, 2s, 4s… capped at 30s, plus jitter.
    const base = Math.min(1000 * 2 ** (attempts - 1), maxBackoffMs)
    reconnectTimer = setTimeout(() => void connect(), base + Math.random() * base * 0.3)
  }

  function disconnect() {
    clearTimeout(reconnectTimer)
    clearTimeout(stableTimer)
    attempts = 0
    const ws = socket
    socket = null
    ws?.close()
  }

  function onVisibilityChange() {
    if (document.hidden) {
      hiddenTimer = setTimeout(disconnect, hiddenGraceMs)
    } else {
      clearTimeout(hiddenTimer)
      void connect()
    }
  }

  /**
   * Asks for a fresh presence snapshot when a user's status is unknown, e.g.
   * an order made them a counterpart after the socket connected.
   */
  function ensurePresence(userId: string) {
    if (presenceOf(userId) || socket?.readyState !== WebSocket.OPEN) return
    socket.send(JSON.stringify({ type: 'sync' }))
  }

  function start() {
    if (running) return
    running = true
    document.addEventListener('visibilitychange', onVisibilityChange)
    void connect()
  }

  function stop() {
    running = false
    document.removeEventListener('visibilitychange', onVisibilityChange)
    clearTimeout(hiddenTimer)
    disconnect()
    unread.value = []
    presence.value = {}
  }

  return {
    unread,
    unreadCount,
    activityFor,
    presenceOf,
    ensurePresence,
    focusOrder,
    onNotification,
    start,
    stop,
  }
})
